import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  canonical?: string;
  noindex?: boolean;
  nofollow?: boolean;
}

/**
 * SEO Component - Dinamik meta tag yönetimi için
 * React 19 uyumlu, bağımlılık gerektirmez
 */
export const SEO: React.FC<SEOProps> = ({
  title = 'Teknik Servis Online Program - tservis | CRM Online',
  description = 'Teknik servis online program - tservis. Kapsamlı teknik servis yönetim ve müşteri takip sistemi. Servis kayıtları, stok yönetimi, müşteri takibi ve raporlama. CRM online çözümü.',
  keywords = 'teknik servis, teknik servis online, teknikservis, teknikservis online program, crm online, tservis, müşteri takibi, stok yönetimi, servis kayıtları, raporlama, teknik servis yönetim sistemi, servis takip programı',
  image = 'https://tservis.tr/logo512.png',
  url = 'https://tservis.tr',
  type = 'website',
  canonical,
  noindex = false,
  nofollow = false
}) => {
  useEffect(() => {
    const baseUrl = 'https://tservis.tr';
    const fullUrl = canonical || url || window.location.href;
    const fullImage = image.startsWith('http') ? image : `${baseUrl}${image}`;

    // Title
    document.title = title;

    // Meta tags
    const updateMetaTag = (name: string, content: string, attribute: string = 'name') => {
      let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('author', 'Teknik Servis Yönetim Sistemi');

    // Robots
    const robotsContent = [
      noindex ? 'noindex' : 'index',
      nofollow ? 'nofollow' : 'follow'
    ].join(', ');
    updateMetaTag('robots', robotsContent);

    // Open Graph
    updateMetaTag('og:type', type, 'property');
    updateMetaTag('og:url', fullUrl, 'property');
    updateMetaTag('og:title', title, 'property');
    updateMetaTag('og:description', description, 'property');
    updateMetaTag('og:image', fullImage, 'property');
    updateMetaTag('og:site_name', 'tservis.tr', 'property');
    updateMetaTag('og:locale', 'tr_TR', 'property');

    // Twitter Card
    updateMetaTag('twitter:card', 'summary_large_image', 'property');
    updateMetaTag('twitter:url', fullUrl, 'property');
    updateMetaTag('twitter:title', title, 'property');
    updateMetaTag('twitter:description', description, 'property');
    updateMetaTag('twitter:image', fullImage, 'property');

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', fullUrl);

    // Cleanup function (optional, but good practice)
    return () => {
      // Meta tags will persist, which is usually desired for SEO
      // But we can reset title if needed
    };
  }, [title, description, keywords, image, url, type, canonical, noindex, nofollow]);

  return null; // This component doesn't render anything
};

export default SEO;

